import React from "react";
import { Box, Typography, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  CaseSheetSection,
  labelSx,
  rowSx,
  inputSx,
  SURFACE,
} from "./CaseSheetModalComponents";

const tobaccoTypeOptions = ["Smoking", "Chewing", "Both"];
const quittingStageOptions = [
  "Pre-contemplation",
  "Contemplation",
  "Preparation",
];

/** Same grid/typography as the editable rows, but visually flagged as non-editable. */
const readOnlyInputSx = {
  ...inputSx,
  "& .MuiOutlinedInput-root": {
    ...inputSx["& .MuiOutlinedInput-root"],
    backgroundColor: SURFACE,
  },
};

const ReadOnlyRow = ({ label, value }) => (
  <Box sx={rowSx}>
    <Typography sx={labelSx}>{label}</Typography>
    <TextField
      size="small"
      value={value || "—"}
      fullWidth
      InputProps={{ readOnly: true }}
      inputProps={{ "aria-label": label, tabIndex: -1 }}
      sx={readOnlyInputSx}
    />
  </Box>
);

const TobaccoCessationTab = ({
  formData,
  isFetching,
  patientInfo,
  onFormDateChange,
  renderTextField,
  renderYesNoField,
  renderOptionToggleField,
  onTriedQuittingBeforeChange,
}) => {
  const quitAttemptDetailsEnabled =
    formData.tobacco_tried_quitting_before === "Yes";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* ── Intake header — demographics come from the patient record ── */}
      <CaseSheetSection title="PATIENT DETAILS" defaultExpanded>
        <ReadOnlyRow label="Name" value={patientInfo?.name} />
        <ReadOnlyRow label="Age" value={patientInfo?.age} />
        <ReadOnlyRow label="Gender" value={patientInfo?.gender} />
        <Box sx={rowSx}>
          <Typography sx={labelSx}>Date</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={
                formData.tobacco_form_date
                  ? dayjs(formData.tobacco_form_date)
                  : null
              }
              onChange={(date) =>
                onFormDateChange(
                  date && date.isValid() ? date.format("YYYY-MM-DD") : ""
                )
              }
              disabled={isFetching}
              slotProps={{
                textField: { size: "small", fullWidth: true, sx: inputSx },
                field: { clearable: true },
              }}
            />
          </LocalizationProvider>
        </Box>
        {renderTextField(
          "Contact No.",
          "tobacco_contact_no",
          "Contact number",
          false
        )}
      </CaseSheetSection>

      <CaseSheetSection title="TOBACCO USE" defaultExpanded>
        {renderOptionToggleField(
          "Type of Tobacco Used",
          "tobacco_type",
          tobaccoTypeOptions
        )}
        {renderTextField(
          "Details",
          "tobacco_type_details",
          "e.g. cigarettes, gutkha, khaini"
        )}
        {renderTextField(
          "Other Health Conditions",
          "tobacco_other_health_conditions",
          "Other health conditions"
        )}
        {renderOptionToggleField(
          "Stage of Quitting",
          "tobacco_quitting_stage",
          quittingStageOptions
        )}
      </CaseSheetSection>

      <CaseSheetSection title="TOBACCO HISTORY" defaultExpanded>
        {renderTextField(
          "How long have you been consuming tobacco?",
          "tobacco_duration_of_use",
          "Duration of tobacco use"
        )}
        {renderTextField(
          "How did you start using tobacco?",
          "tobacco_initiation_reason",
          "How tobacco use started"
        )}
        {renderTextField(
          "When do you usually get the first urge during the day?",
          "tobacco_first_urge_time",
          "Time of first urge"
        )}
      </CaseSheetSection>

      <CaseSheetSection title="MOTIVATION" defaultExpanded>
        {renderYesNoField(
          "Is quitting tobacco important to you right now?",
          "tobacco_quitting_important"
        )}
        {renderYesNoField(
          "Do you believe you can quit if you decide to?",
          "tobacco_can_quit_belief"
        )}
        {renderYesNoField(
          "Are you willing to make a quit plan?",
          "tobacco_willing_quit_plan"
        )}
      </CaseSheetSection>

      <CaseSheetSection title="PREVIOUS QUIT ATTEMPTS" defaultExpanded>
        {renderYesNoField(
          "Have you tried quitting before?",
          "tobacco_tried_quitting_before",
          onTriedQuittingBeforeChange
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.75, sm: 2.25 },
            pointerEvents: quitAttemptDetailsEnabled ? "auto" : "none",
            opacity: quitAttemptDetailsEnabled ? 1 : 0.45,
            transition: "opacity 0.2s ease",
          }}
        >
          {renderTextField(
            "If yes, how many quit attempts have you made?",
            "tobacco_quit_attempts_count",
            "Number of quit attempts",
            false
          )}
          {renderTextField(
            "What do you think caused the relapse?",
            "tobacco_relapse_cause",
            "Reason for relapse"
          )}
        </Box>
      </CaseSheetSection>

      <CaseSheetSection title="OBSERVATIONS" defaultExpanded>
        {renderTextField(
          "Observations",
          "tobacco_observations",
          "Practitioner observations"
        )}
      </CaseSheetSection>
    </Box>
  );
};

export default TobaccoCessationTab;
